/**
 * Shared bits for the credit-approval request flow (owner spec 2026-10-15):
 * API helper, colours, money formatting and a searchable user picker.
 *
 * Backend: backend/routers/credit_approvals.py
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import config from '../../config/config';
import { getSessionToken } from '../../utils/session';

const API_BASE = config.api.baseUrl;

export async function api(path, { method = 'GET', body } = {}) {
  const token = getSessionToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { data = null; }
  if (!res.ok) {
    const detail = data && (data.detail || data.error);
    throw new Error(typeof detail === 'string' ? detail : `שגיאה (${res.status})`);
  }
  return data;
}

export const ILS = (n) => (n === null || n === undefined || n === '' ? '—'
  : '₪' + Number(n).toLocaleString('he-IL', { maximumFractionDigits: 2 }));

// Chip colours — the same red / green language as the owner's spreadsheet.
export const CREDIT_TYPE_COLOR = {
  'ביטול עסקה': { bg: '#c00000', fg: '#fff' },
  'שימור עסקה': { bg: '#0b7a3e', fg: '#fff' },
  'הנחה': { bg: '#e8eaed', fg: '#202124' },
  'שינוי מסלול': { bg: '#dbeafe', fg: '#1d4ed8' },
  'מניעת תביעה': { bg: '#fef3c7', fg: '#92400e' },
  'חוק פיצוי טכנאי': { bg: '#ede9fe', fg: '#5b21b6' },
};

export const STATUS_COLOR = {
  pending: { bg: '#fef3c7', fg: '#92400e', label: 'ממתין לאישור' },
  awaiting_pickup: { bg: '#ede9fe', fg: '#5b21b6', label: 'ממתין לאיסוף מכשיר' },
  approved: { bg: '#0b7a3e', fg: '#fff', label: 'אושר' },
  rejected: { bg: '#c00000', fg: '#fff', label: 'נדחה' },
  done: { bg: '#1e3a5f', fg: '#fff', label: 'בוצע' },
  retained: { bg: '#e0f2fe', fg: '#075985', label: 'שומר ללא זיכוי' },
};

// Service-appointment status in Hebrew — the labels of SA_STATUS_HE in
// accounting/salesReportHelpers.js (not imported: that module pulls in exceljs).
const SA_STATUS_LABELS = {
  'None': 'ללא סטטוס', 'New': 'חדש', 'Scheduled': 'מתוזמן', 'Dispatched': 'נשלח', 'In Progress': 'בתהליך',
  'Cannot Complete': 'לא ניתן להשלים', 'Canceled': 'בוטל', 'Completed': 'הושלמה',
};
export const saStatusHe = (s) => (s ? SA_STATUS_LABELS[s] || s : '');

export const Chip = ({ text, color, wrap }) => (
  <span style={{
    display: 'inline-block', padding: wrap ? '1px 7px' : '2px 10px', borderRadius: wrap ? 8 : 12,
    fontSize: wrap ? 11 : 12, fontWeight: 600, lineHeight: 1.4,
    background: (color && color.bg) || '#e8eaed', color: (color && color.fg) || '#202124',
    whiteSpace: wrap ? 'normal' : 'nowrap', maxWidth: wrap ? '100%' : undefined,
  }}>{text || '—'}</span>
);

// The installation window a request falls in (owner 2026-10-20) — the report
// filter and the dashboard split. Colours validated for CVD (dataviz).
export const INSTALL_WINDOWS = [
  { key: 'within', label: 'בתוך 14 ימי עסקים', short: 'בתוך', color: '#1d6b3f' },
  { key: 'over', label: 'מעל 14 ימי עסקים', short: 'מעל', color: '#f07a52' },
  { key: 'none', label: 'ללא התקנה או סגירה', short: 'ללא', color: '#8a97ab' },
];
export const installWindow = (r) => (!r.install_date ? 'none' : r.install_within_window ? 'within' : 'over');

/**
 * Installation date + Israeli business days since it (owner 2026-10-20): green
 * inside the 14-business-day window, bold red after it. `days` counts to the
 * request date in the report and to today in the request modal (server side).
 * source 'close_won' (no work order, nothing serialized) = the deal's successful
 * close starts the window instead of an installation — shown as "סגירה <date>".
 */
export const InstallBadge = ({ date, days, within, inline, source }) => {
  if (!date) return <span style={{ color: '#9e9e9e' }}>—</span>;
  const shown = new Date(`${date}T00:00:00`).toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric', year: '2-digit' });
  const after = days != null && days < 0;
  const color = after ? { bg: '#e8eaed', fg: '#3e3e3c' } : within ? { bg: '#dcfce7', fg: '#166534' } : { bg: '#c00000', fg: '#fff' };
  return (
    <span style={{ display: 'inline-flex', flexDirection: inline ? 'row' : 'column', alignItems: inline ? 'center' : 'flex-start', gap: inline ? 8 : 2 }}>
      <span>{source === 'close_won' && <span style={{ color: '#706e6b' }}>סגירה </span>}{shown}</span>
      {days != null && (
        <span title="ימי עסקים בישראל (ללא שישי, שבת וחגים)" style={{
          padding: '1px 7px', borderRadius: 8, fontSize: 11, lineHeight: 1.4, background: color.bg, color: color.fg,
          fontWeight: within || after ? 600 : 800,
        }}>{after ? 'ההתקנה אחרי הבקשה' : `${days} ימי עסקים`}</span>
      )}
    </span>
  );
};

export const userLabel =(u) => (u && (u.name || `${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim()
  || u.username || u.email)) || '';

/**
 * Searchable single-user picker. `users` = [{username, name, email, ...}];
 * `value` = selected username; `onChange(user | null)`.
 */
export function UserPicker({ users, value, onChange, placeholder = 'חיפוש משתמש...', invalid }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const selected = users.find(u => u.username === value) || null;

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? users.filter(u => `${userLabel(u)} ${u.username || ''} ${u.email || ''}`.toLowerCase().includes(q)) : users;
    return list.slice(0, 50);
  }, [users, query]);

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        type="text"
        value={open ? query : (selected ? userLabel(selected) : '')}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        style={{ ...inputStyle, borderColor: invalid ? '#c00000' : inputStyle.borderColor }}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', insetInlineStart: 0, insetInlineEnd: 0, zIndex: 20,
          maxHeight: 220, overflowY: 'auto', background: '#fff', border: '1px solid #d8dde6',
          borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,.12)', marginTop: 2,
        }}>
          {matches.length === 0 && <div style={{ padding: 10, color: '#706e6b', fontSize: 13 }}>לא נמצאו משתמשים</div>}
          {matches.map(u => (
            <div
              key={u.username}
              onMouseDown={(e) => { e.preventDefault(); onChange(u); setOpen(false); }}
              style={{ padding: '8px 10px', cursor: 'pointer', fontSize: 13, background: u.username === value ? '#eef4ff' : '#fff' }}
            >
              <div style={{ fontWeight: 600 }}>{userLabel(u)}</div>
              <div style={{ fontSize: 11, color: '#706e6b' }}>{u.email || u.username}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 14,
  border: '1px solid #c9c9c9', borderColor: '#c9c9c9', borderRadius: 6, background: '#fff', fontFamily: 'inherit',
};

export const readOnlyStyle = {
  ...inputStyle, background: '#f3f3f3', color: '#3e3e3c', minHeight: 36,
};

export const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#3e3e3c', marginBottom: 4 };

export const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 10000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};

export const modalStyle = {
  background: '#fff', borderRadius: 10, width: '100%', maxWidth: 760, maxHeight: '92vh',
  display: 'flex', flexDirection: 'column', boxShadow: '0 10px 40px rgba(0,0,0,.25)', direction: 'rtl',
};

export const btn = (bg, fg = '#fff', border = 'none') => ({
  padding: '8px 18px', borderRadius: 6, border, background: bg, color: fg,
  fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
});
